import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { ArrowLeft, Bell, Megaphone, X, CheckCheck } from 'lucide-react';
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkNotificationDismissed,
} from '@/hooks/useNotifications';
import {
  markNotificationRead, fetchActiveAnnouncements, dismissAnnouncement,
  type NotificationView,
} from '@/lib/api';
import type { AnnouncementPublic } from '@evi/shared';
import { useAnnouncementsStore } from '@/store/announcements';
import { toast } from '@/store/toast';
import { useTranslation } from 'react-i18next';
import NamedIcon from '@/components/ui/NamedIcon';
import styles from './NotificationsPage.module.css';

interface Props { onNavigate: (path: string) => void }

type NotificationsT = ReturnType<typeof useTranslation>['t'];

/**
 * /notifications — full-page list reached from Profile menu.
 * Mobile-native pattern: a dedicated page rather than a desktop-
 * style dropdown. Reads from the existing /admin/notifications
 * endpoints (target_resolver gates per-user data; no /me
 * mirror needed).
 *
 * Click behavior on a row:
 *   1. Optimistically mark read (the row's unread indicator
 *      clears immediately; the mutation rolls back on error).
 *   2. Navigate to notification.link if present.
 *   3. If link is null, stay on the page (notification is
 *      now read).
 *
 * Dismiss × removes the row optimistically. The Mongo doc gets
 * dismissed_by[user] = now, so the row stops appearing in the
 * list query going forward (the API filter excludes dismissed).
 *
 * Hard cap of 50 rows for v1 — pagination is filed as polish.
 */
export function NotificationsPage({ onNavigate }: Props) {
  const { t } = useTranslation('notifications');
  const q = useNotifications();
  const markReadMut = useMarkNotificationRead();
  const dismissMut = useMarkNotificationDismissed();

  const items = q.data ?? [];
  const unreadCount = useMemo(
    () => items.filter((n) => n.read_at === null).length,
    [items],
  );

  // Same two tabs as the bell, same server-stamped `tab` (see
  // NotificationBell.tsx): Activity = what the driver can act on; News &
  // rewards = what we put in front of them. Announcements live under News.
  const [tab, setTab] = useState<'activity' | 'news'>('activity');
  const tabItems = useMemo(
    () => items.filter((n) => (tab === 'news' ? n.tab === 'news' : n.tab !== 'news')),
    [items, tab],
  );
  const activityUnread = useMemo(() => items.filter((n) => n.tab !== 'news' && n.read_at === null).length, [items]);
  const newsUnreadRows = useMemo(() => items.filter((n) => n.tab === 'news' && n.read_at === null).length, [items]);

  // Active announcements — same shared query key as the bell + overlay, so a
  // dismissal anywhere reflects here. Opening this page counts as seeing them.
  const qc = useQueryClient();
  const presentAnnouncement = useAnnouncementsStore((s) => s.present);
  const markBellSeen = useAnnouncementsStore((s) => s.markBellSeen);
  const annQ = useQuery({
    queryKey: ['announcements', 'active'],
    queryFn: () => fetchActiveAnnouncements().catch(() => []),
    staleTime: 5 * 60_000,
  });
  const [annDismissed, setAnnDismissed] = useState<Set<string>>(new Set());
  const dismissAnnMut = useMutation({
    mutationFn: dismissAnnouncement,
    onMutate: (id: string) => setAnnDismissed((prev) => new Set(prev).add(id)),
    onError: (_e, id) => setAnnDismissed((prev) => {
      const next = new Set(prev); next.delete(id); return next;
    }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['announcements', 'active'] }),
  });
  const announcements = useMemo<AnnouncementPublic[]>(
    () => (annQ.data ?? []).filter((a) => !annDismissed.has(a.id)),
    [annQ.data, annDismissed],
  );

  useEffect(() => {
    if (annQ.data && annQ.data.length > 0) markBellSeen(annQ.data.map((a) => a.id));
  }, [annQ.data, markBellSeen]);
  // Prune the optimistic layer to ids the server still returns — a same-data
  // background refetch mid-POST can't resurrect a just-dismissed row.
  useEffect(() => {
    if (!annQ.data) return;
    const present = new Set(annQ.data.map((a) => a.id));
    setAnnDismissed((prev) => {
      const next = new Set([...prev].filter((id) => present.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [annQ.data]);

  const openAnnouncement = (a: AnnouncementPublic) => {
    // Re-present the overlay on the map (where it lives).
    presentAnnouncement(a.id);
    onNavigate('/map');
  };

  const dismissAnnouncementRow = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    dismissAnnMut.mutate(id);
  };

  const handleRowClick = (n: NotificationView) => {
    // Mark read first (optimistic), then navigate. The mutation
    // is fire-and-forget from the UI's perspective — it's already
    // visually marked read.
    if (n.read_at === null) {
      markReadMut.mutate(n._id);
    }
    if (n.link) {
      onNavigate(n.link);
    }
  };

  const handleDismiss = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    dismissMut.mutate(id);
  };

  const handleMarkAllRead = async () => {
    const unread = items.filter((n) => n.read_at === null);
    if (unread.length === 0) return;
    // These call the RAW api (no per-mutation cache side effects), so after the
    // batch we must invalidate the whole ['notifications'] tree ourselves —
    // otherwise the shared ['notifications','unread-count'] cache the top-bar
    // bell badge reads stays stale until its 30s poll. Failures roll back
    // per-row; partial success is still a win.
    try {
      await Promise.all(unread.map((n) => markNotificationRead(n._id)));
      await qc.invalidateQueries({ queryKey: ['notifications'] });
      toast(t('toast.markedAll'));
    } catch {
      // Partial-failure surface — warn tone, not the default neutral,
      // because the batch didn't complete cleanly. Active voice on the
      // copy ("Couldn't mark some" instead of "could not be marked").
      toast(t('toast.markSomeFailed'), { tone: 'warn' });
    }
  };

  return (
    <div className={styles.page}>
      <button className={styles.backFab} onClick={() => onNavigate('/profile')} aria-label={t('back')}>
        <ArrowLeft size={16} />
      </button>

      <div className={styles.header}>
        <h1 className={styles.title}>{t('title')}</h1>
        {unreadCount > 0 && (
          <button
            type="button"
            className={styles.markAllBtn}
            onClick={() => void handleMarkAllRead()}
          >
            <CheckCheck size={14} /> {t('markAllRead')}
          </button>
        )}
      </div>

      <div className={styles.tabs} role="tablist" aria-label={t('title')}>
        {(['activity', 'news'] as const).map((key) => {
          const count = key === 'activity' ? activityUnread : newsUnreadRows + announcements.length;
          const selected = tab === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={selected}
              className={`${styles.tab} ${selected ? styles.tabSelected : ''}`}
              onClick={() => setTab(key)}
            >
              {t(`tabs.${key}`)}
              {count > 0 && <span className={styles.tabCount}>{count > 99 ? '99+' : count}</span>}
            </button>
          );
        })}
      </div>

      {q.isLoading && (
        <div className={styles.skeleton}>
          <div className={styles.skeletonRow} />
          <div className={styles.skeletonRow} />
          <div className={styles.skeletonRow} />
        </div>
      )}

      {/* Announcements — admin-published, above transactional notifications.
          Tap re-opens the overlay on the map; the X permanently dismisses. */}
      {tab === 'news' && announcements.length > 0 && (
        <div className={styles.list}>
          {announcements.map((a) => (
            <button
              key={`ann-${a.id}`}
              type="button"
              className={`${styles.row} ${styles.announceRow}`}
              onClick={() => openAnnouncement(a)}
            >
              <div className={styles.body}>
                <span className={styles.announceEyebrow}>
                  <Megaphone size={11} /> {t('announcement')}
                </span>
                <p className={styles.rowTitle}>{a.title}</p>
                <p className={styles.rowBody}>{a.body}</p>
              </div>
              <button
                type="button"
                className={styles.dismissBtn}
                onClick={(e) => dismissAnnouncementRow(e, a.id)}
                aria-label={t('dismiss')}
                title={t('dismiss')}
              >
                <X size={14} />
              </button>
            </button>
          ))}
        </div>
      )}

      {!q.isLoading && tabItems.length === 0 && (tab === 'activity' || announcements.length === 0) && (
        <div className={styles.emptyCard}>
          <span className={styles.emptyIconWrap}>
            <NamedIcon name="bell" fallback={<Bell size={36} />} size={36} />
          </span>
          <p className={styles.emptyTitle}>{t(tab === 'news' ? 'emptyNews.title' : 'empty.title')}</p>
          <p className={styles.emptyDesc}>
            {t(tab === 'news' ? 'emptyNews.desc' : 'empty.desc')}
          </p>
        </div>
      )}

      {!q.isLoading && tabItems.length > 0 && (
        <div className={styles.list}>
          {tabItems.map((n) => {
            const isUnread = n.read_at === null;
            return (
              <button
                key={n._id}
                type="button"
                className={`${styles.row} ${isUnread ? styles.rowUnread : ''}`}
                onClick={() => handleRowClick(n)}
              >
                {isUnread && <span aria-hidden className={styles.unreadStripe} />}
                <div className={styles.body}>
                  <p className={styles.rowTitle}>{n.title}</p>
                  <p className={styles.rowBody}>{n.body}</p>
                  {/* Time of the LATEST occurrence, and "×N" when the server
                      folded repeats onto this row (a flapping charger). */}
                  <span className={styles.rowMetaLine}>
                    <span className={styles.rowTime}>
                      {formatRelativeTime(n.last_occurred_at ?? n.created_at, t)}
                    </span>
                    {n.occurrence_count > 1 && (
                      <span
                        className={styles.repeatChip}
                        aria-label={t('repeatLabel', { count: n.occurrence_count })}
                      >
                        {t('repeat', { count: n.occurrence_count })}
                      </span>
                    )}
                  </span>
                </div>
                <button
                  type="button"
                  className={styles.dismissBtn}
                  onClick={(e) => handleDismiss(e, n._id)}
                  aria-label={t('dismiss')}
                  title={t('dismiss')}
                >
                  <X size={14} />
                </button>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Compact relative-time formatter — matches the Friend list
 *  treatment for cross-surface consistency. "5m ago" / "2h ago"
 *  / "Mon 14" past-day. */
function formatRelativeTime(iso: string, t: NotificationsT): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  if (diffMs < 60_000) return t('time.justNow');
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return t('time.minutes', { m: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('time.hours', { h: hours });
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
